import { CheckIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { t } from "@lingui/core/macro";
import { type ReactElement, useState } from "react";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/shared/components/ui/tooltip";

type PasswordValidationTooltipProps = {
  password: string;
  children: ReactElement;
};

export const PASSWORD_VALIDATION_RULES = [
  {
    label: t`At least 8 characters`,
    regex: /.{8,}/,
  },
  {
    label: t`At least 1 uppercase letter`,
    regex: /[A-Z]/,
  },
  {
    label: t`At least 1 lowercase letter`,
    regex: /[a-z]/,
  },
  {
    label: t`At least 1 number`,
    regex: /[0-9]/,
  },
  {
    label: t`At least 1 special character`,
    regex: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/,
  },
  {
    label: t`No spaces`,
    regex: /^\S*$/,
  },
];

export default function PasswordValidationTooltip({ password, children }: PasswordValidationTooltipProps) {
  const [focused, setFocused] = useState(false);
  const rulesStatus = PASSWORD_VALIDATION_RULES.map(({ label, regex }) => ({
    label,
    valid: regex.test(password),
  }));

  return (
    <TooltipProvider>
      <Tooltip open={password.length > 0 && focused}>
        <TooltipTrigger render={children} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />
        <TooltipContent side="bottom" className="p-4">
          <div className="flex flex-col">
            <p className="mb-2 text-sm font-medium text-charcoal">{t`Password must contain:`}</p>
            <ul className="list-inside list-disc">
              {rulesStatus.map(({ label, valid }) => (
                <li key={label} className="mb-2 flex list-none items-center gap-1">
                  {valid ? (
                    <CheckIcon className="h-4 w-auto text-green-500" />
                  ) : (
                    <XMarkIcon className="h-4 w-auto text-red-500" />
                  )}
                  {label}
                </li>
              ))}
            </ul>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
